import SelectField from 'components/SelectField';
import AppContext from 'contexts/AppContext';
import { useFormik } from 'formik';
import { MetricsForm } from 'forms/schemas/MetricsForm';
import moment from 'moment';
import React from 'react';
import { CalendarToday } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, Tooltip, BarChart, Bar, PieChart, Pie, CartesianGrid, YAxis, Cell } from 'recharts';
import { getMetricBillingTotal, getMetricSalesByCommerce } from 'services/orderServices';
import { getAllVenues } from 'services/venuesServices';
import 'styles/pages/WelcomePage.scss';
import UserRoleType from 'types/enums/UserRoleType';
import Commerce from 'types/models/Commerce';
import BillingByVenue, { BestSellingProduct } from 'types/models/MetricData';
import Venue from 'types/models/Venue';
import { valueToCurrencyFormat } from 'utils/parseCurrency';

interface GraphicValue {
  name: string;
  value: number;
  venueId?: string;
}

interface CommerceSalesRanking {
  commerce: Commerce;
  sales: number;
  bestSellingProducts: BestSellingProduct[];
}

export default (): JSX.Element => {
  const { t } = useTranslation();
  const { authData } = React.useContext(AppContext);
  const [venues, setVenues] = React.useState<Venue[] | null>(null);
  const [metrics, setMetrics] = React.useState<BillingByVenue[]>([]);
  const [donutValues, setDonutValues] = React.useState<GraphicValue[]>([]);
  const [barGraphicValues, setBarGraphicValues] = React.useState<GraphicValue[]>([]);
  const [commerceSalesRanking, setCommerceSalesRanking] = React.useState<CommerceSalesRanking[]>([]);
  const [selectedVenue, setSelectedVenue] = React.useState<string | null>(null);
  const COLORS = [
    '#E67399',
    '#B872E6',
    '#729EE6',
    '#72E6D1',
    '#72E687',
    '#B8E672',
    '#E6C472',
    '#E69E72',
    '#E6A6D3',
    '#E6C4A6',
    '#C6A6E6',
    '#9EC4E6',
    '#A6E6C4',
    '#A6E69E',
    '#C4E6A6',
    '#E6A6C4',
    '#9EA6D8',
    '#E69ECF',
    '#E67373',
    '#C49EE6',
    '#D3A6E6',
    '#E6D3A6',
    '#E6A6A6',
    '#D8B89E',
    '#E6D89E',
    '#A6D89E',
    '#E69EC4',
    '#A69EE6',
    '#9EB89E',
    '#C49EA6',
    '#A6C49E',
    '#C4A69E',
    '#9E9EE6',
    '#D8E69E',
    '#E6A69E',
    '#E6D8A6',
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip-container">
          <p className="custom-tooltip-title">{payload[0].payload.name}</p>
          <p>{`Facturación: ${valueToCurrencyFormat(payload[0].value)}`}</p>
        </div>
      );
    }

    return null;
  };

  const venuesForSelect = (venues: Venue[]) => {
    return venues.map((venue) => {
      return {
        label: venue.name,
        value: venue.id,
      };
    });
  };

  const formik = useFormik({
    initialValues: {
      venues: [],
      dateFrom: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      dateTo: moment().format('YYYY-MM-DD HH:mm:ss'),
    },
    validationSchema: MetricsForm(t),
    onSubmit: () => {
      console.log();
      formik.setSubmitting(false);
    }, //vacío porque sólo se va a activar con cada cambio,
  });

  const formatForInput = (dateValue: any) => {
    if (!dateValue) return '';
    return moment(dateValue).format('YYYY-MM-DDTHH:mm');
  };

  const searchMetrics = async () => {
    type VenueData = {
      commerceIds: string[];
      localTimezoneIdentifier: string;
      dateFrom: string;
      dateTo: string;
    };

    const body: { venues: VenueData[] } = { venues: [] };
    formik.values.venues.forEach((venueId) => {
      const venue = venues?.find(({ id }) => id === venueId);
      if (venue) {
        const venueId =
          authData?.user.role === UserRoleType.VenueEditor ? getVenueByVenueEditor(authData?.user.id)?.id : venue.id;
        const commerceIds = getCommerceIdsForRequest(venue);
        const bodyVenue = {
          commerceIds,
          localTimezoneIdentifier: venue.timeZoneIdentifier ?? '',
          dateFrom: formik.values.dateFrom.toString(),
          dateTo: formik.values.dateTo.toString(),
          venueId,
        };
        body.venues.push(bodyVenue);
      }
    });

    try {
      const response = await getMetricBillingTotal(body);
      if (response) {
        setMetrics(response);
        parseDataForDonut(response);
      }

      // Buscamos el ranking de commerces
      getCommerceSalesRanking(body);
    } catch (error) {
      console.log('error al obtener metrics', error);
    }
  };

  const getCommerceIdsForRequest = (venue: Venue): string[] => {
    let commerceIds = venue.commerces?.map(({ id }) => id) ?? [];
    if (authData?.user.role === UserRoleType.CommerceEditor) {
      const foundedCommerce = venue.commerces?.find(({ editorId }) => editorId === authData.user.id);
      if (foundedCommerce) {
        commerceIds = [foundedCommerce.id];
      }
    }

    return commerceIds;
  };

  const getTotalSalesByVenue = (): number => {
    const total = commerceSalesRanking?.reduce((acc, { sales }) => {
      return acc + sales;
    }, 0);

    return total;
  };

  const getCommerceSalesRanking = async (body: any) => {
    const salesByCommercesData: CommerceSalesRanking[] = [];
    const userVenue = venues?.find(({ editorId, id }) => {
      if (selectedVenue) return id === selectedVenue;
      return editorId === authData?.user.id;
    });

    // Si tenemos más de un venue, entonces solamente vamos a usar el venue seleccionado
    if (body.venues.length > 1) body.venues = body.venues.filter((item: any) => item.venueId === selectedVenue);

    const salesByCommercesResponse = await getMetricSalesByCommerce(body);
    if (!salesByCommercesResponse || !userVenue) return;

    userVenue.commerces?.forEach((item) => {
      const sales = salesByCommercesResponse.totalSales.find(({ id }) => id === item.id)?.totalSales ?? 0;
      const bestSellingProducts = salesByCommercesResponse.bestSellingProducts.filter(
        ({ commerceId }) => commerceId === item.id,
      );
      const commerceValue = { commerce: item, sales, bestSellingProducts };
      salesByCommercesData.push(commerceValue);
    });

    setCommerceSalesRanking(salesByCommercesData);
  };

  const getVenueByVenueEditor = (userId: string): Venue | undefined => {
    return venues?.find(({ editorId }) => editorId === userId);
  };

  const parseDataForDonut = (data: BillingByVenue[]) => {
    const finalDonutValues: GraphicValue[] = [];
    data.forEach((venue) => {
      const foundedVenue = venues?.find(({ id }) => id === venue.venueId);
      if (foundedVenue) {
        const value = { name: foundedVenue.name, value: venue.totalBilling, venueId: foundedVenue.id };
        finalDonutValues.push(value);
      }
    });

    setDonutValues(finalDonutValues);
    if (!selectedVenue) setSelectedVenue(data[0].venueId);
  };

  const getTotalFacturation = () => {
    let total = 0;
    barGraphicValues.forEach((item) => (total += item.value));

    return valueToCurrencyFormat(total);
  };

  const getVenueColor = (venueId: string, index: number): string => {
    const foundedVenue = venues?.find(({ id }) => id === venueId);
    if (foundedVenue && foundedVenue.appColor) return foundedVenue.appColor;

    return COLORS[index % COLORS.length];
  };

  const everyVenuesWithoutData = (): boolean => {
    return donutValues.every(({ value }) => value === 0);
  };

  const getAverageBySales = (id: string, sales: number): number => {
    const venueMetric = metrics.find(({ venueId }) => venueId === selectedVenue);
    let average = 0;
    if (venueMetric) {
      const commerce = venueMetric.commerces.find(({ commerceId }) => commerceId === id);
      if (commerce) {
        average = commerce.billing / sales;
      }
    }
    return average;
  };

  const getVenueName = (): string => {
    const venue = venues?.find(({ id }) => id === selectedVenue);
    return venue?.name ?? '';
  };

  React.useEffect(() => {
    const getVenues = async () => {
      const allVenues = await getAllVenues();
      setVenues(allVenues);

      // Si somos editores de venue o de comercio, buscamos el venueId del que pertenecemos
      let foundedVenueId = null;
      if (authData?.user.role === UserRoleType.VenueEditor) {
        // Si el usuario logueado es editor de venue, entonces buscamos entre todos los venues
        // el venue que lo tenga como editor
        foundedVenueId = allVenues?.find(({ editorId }) => editorId === authData?.user.id)?.id;
      }

      if (authData?.user.role === UserRoleType.CommerceEditor) {
        // Si el usuario logueado es editor de comercio, entonces buscamos entre todos los venues
        // si tienen un comercio en donde el usuario logueado sea editor
        foundedVenueId = allVenues?.find(({ commerces }) =>
          commerces?.some(({ editorId }) => editorId === authData.user.id),
        )?.id;
      }

      if (foundedVenueId) {
        formik.setFieldValue('venues', [foundedVenueId]);
        setSelectedVenue(foundedVenueId);
      }
    };
    getVenues();
  }, []);

  React.useEffect(() => {
    const venue = venues?.find(({ id }) => id === selectedVenue);
    const metric = metrics?.find(({ venueId }) => venueId === selectedVenue);
    if (!metric) return;

    const commerceMetric: GraphicValue[] = metric.commerces.map((item) => {
      const commerceName = venue?.commerces?.find(({ id }) => id === item.commerceId)?.name;
      return { name: commerceName ?? '', value: item.billing };
    });

    // Ordenamos los comercios alfabéticamente
    const sortedData = [...commerceMetric].sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0; // nombres iguales
    });

    setBarGraphicValues(sortedData);
  }, [selectedVenue, metrics]);

  React.useEffect(() => {
    if (formik.values.venues.length === 0) return;

    searchMetrics();
    const intervalId = setInterval(() => {
      searchMetrics();
    }, 90000); // cada 90 segundos

    return () => {
      clearInterval(intervalId);
    };
  }, [formik.values, selectedVenue]);

  return (
    <div className="full-container">
      <section className="content">
        <form onSubmit={formik.handleSubmit}>
          {venues && authData?.user.role === UserRoleType.Admin && (
            <SelectField
              name="venues"
              label={t('app-header-venues')}
              placeholder={t('venue-form-sections-placeholder')}
              value={formik.values.venues}
              options={venuesForSelect(venues)}
              isMulti={true}
              setFieldValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
              touched={formik.touched.venues}
              error={formik.errors.venues}
              isClearable={true}
              backspaceRemovesValue={true}
              isDisabled={formik.isSubmitting}
              isSearchable={true}
            />
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px',
              background: '#f5f5f5',
              borderRadius: '10px',
              flexWrap: 'wrap',
            }}
          >
            {/* START DATE */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '6px 10px',
                background: 'white',
                cursor: 'pointer',
                minWidth: '200px',
              }}
            >
              <CalendarToday style={{ color: '#666' }} />
              <span style={{ color: formik.values.dateFrom ? '#000' : '#999' }}>
                {formik.values.dateFrom
                  ? moment(formik.values.dateFrom).format('DD/MM/YYYY HH:mm')
                  : 'Seleccione fecha de inicio'}
              </span>
              <input
                id="dateInputModified"
                type="datetime-local"
                name="dateFrom"
                value={formatForInput(formik.values.dateFrom)}
                onChange={(event) =>
                  formik.setFieldValue('dateFrom', moment(event.target.value).format('YYYY-MM-DD HH:mm'))
                }
                style={{
                  position: 'absolute',
                  minWidth: 200,
                  opacity: 0,
                }}
              />
            </div>

            <span style={{ color: '#666' }}>—</span>

            {/* END DATE */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '6px 10px',
                background: 'white',
                cursor: 'pointer',
                minWidth: '200px',
              }}
            >
              <CalendarToday style={{ color: '#666' }} />
              <span style={{ color: formik.values.dateTo ? '#000' : '#999' }}>
                {formik.values.dateTo
                  ? moment(formik.values.dateTo).format('DD/MM/YYYY HH:mm')
                  : 'Seleccione fecha de fin'}
              </span>
              <input
                id="dateInputModified"
                type="datetime-local"
                name="dateTo"
                value={formatForInput(formik.values.dateFrom)}
                onChange={(event) =>
                  formik.setFieldValue('dateTo', moment(event.target.value).format('YYYY-MM-DD HH:mm'))
                }
                style={{
                  position: 'absolute',
                  minWidth: 200,
                  opacity: 0,
                }}
              />
            </div>
          </div>
        </form>

        <div style={{ width: '100%', height: 300, marginBottom: 20, borderWidth: 1, borderColor: '#f00' }}>
          <div className="graphics-container">
            {authData?.user.role === UserRoleType.Admin && !everyVenuesWithoutData() && (
              <div className="donut-graphic-container">
                {selectedVenue && (
                  <div className="subtitle-container">
                    <span className="title-container">{getVenueName()}</span>
                  </div>
                )}

                <ResponsiveContainer width="100%" height="100%">
                  <PieChart style={{ width: '100%', height: '100%' }}>
                    <Pie
                      onClick={(item) => setSelectedVenue(item.payload.venueId)}
                      dataKey="value"
                      isAnimationActive={false}
                      data={donutValues}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                    >
                      {donutValues.map((entry, index) => (
                        <Cell
                          className="cellDonut"
                          key={`cell-${index}`}
                          fill={getVenueColor(entry.venueId as string, index)}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {barGraphicValues && !everyVenuesWithoutData() && (
              <div className="bar-graphic-container">
                <div className="subtitle-container">
                  {t('consumptionsReport-page-total')} <span className="title-container">{getTotalFacturation()}</span>
                </div>
                <ResponsiveContainer width="100%" height="85%" style={{ borderWidth: 1 }}>
                  <BarChart
                    width={800}
                    height={800}
                    data={barGraphicValues}
                    margin={{
                      top: 20,
                      right: 40,
                      left: 40,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <YAxis tickFormatter={(value) => valueToCurrencyFormat(value)} style={{ fontSize: 14 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" stackId="a" fill="#8884d8">
                      {barGraphicValues.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % 20]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="graphics-container">
            {commerceSalesRanking.length > 0 && selectedVenue && !everyVenuesWithoutData() && (
              <>
                <div className="commerce-ranking-container">
                  <div className="title-container">{t('dashboard-page-commerces-sales-ranking')}</div>
                  <div className="subtitle-container">
                    {t('consumptionsReport-page-total')}:{' '}
                    <span className="title-container">{getTotalSalesByVenue()}</span>
                  </div>
                  {commerceSalesRanking.map((item, index) => (
                    <div key={index} className="commerce-container">
                      <div className="item">
                        <div className="commerce-name-title fw-bold">{item.commerce.name}</div>
                        <div className="commerce-sales">{item.sales}</div>
                      </div>
                      {item.sales > 0 && (
                        <div className="item">
                          <div className="commerce-name-title">Promedio de compra</div>
                          <div className="commerce-sales average-sales">
                            {valueToCurrencyFormat(getAverageBySales(item.commerce.id, item.sales))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="commerce-ranking-container">
                  <div className="title-container">{t('dashboard-page-products-sold-ranking')}</div>

                  {commerceSalesRanking.map((item, index) => (
                    <div key={index} className="commerce-container">
                      <div className="commerce-name-title fw-bold">{item.commerce.name}</div>
                      {item.bestSellingProducts.length > 0 &&
                        item.bestSellingProducts
                          .sort((a, b) => b.totalSale - a.totalSale)
                          .slice(0, 5)
                          .map((item, index) => (
                            <div className="item" key={index}>
                              <div className="commerce-name-title">{item.productName}</div>
                              <div className="commerce-sales">{item.totalSale}</div>
                            </div>
                          ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
